<template>
  <div class="form">
    <h1 class="extra-large-headline" v-if="isOrderPreparing">
      {{  $t('CoinDepositForm.label.verify') }}
    </h1>
    <h1 class="extra-large-headline" v-else>{{ $t('CoinDepositForm.label.deposit') }}</h1>
    <form v-if="!isOrderPreparing" @submit.prevent>
      <component
        :is="loadDynamicFormPath"
        :orderData="orderData"
        :orderId="orderId"
        @deposit-complete="handleFormRedirect"
      />
    </form>
    <p v-else>
      {{ $t('CoinDepositForm.label.pleaseWait') }}
    </p>
  </div>
</template>

<script setup>
import {
 ref, onMounted, computed, defineAsyncComponent, reactive,
} from 'vue';
import { useRouter, useRoute } from "vue-router";
import { useI18n } from 'vue-i18n';

import { useGlobalStore } from "@/stores/globalStore";
import { useErrorStore } from '@/stores/components/errorStore';
import useOrderEndpoints from "@/composables/api/useOrderEndpoints";
import useRedirectPayments from '@/composables/payments/useRedirectPayment';

import useApiHelpers from "@/composables/api/helpers";
import { capitaliseFirstChar } from "@/composables/helpers/useStringHelpers.js";

const globalStore = useGlobalStore();
const router = useRouter();
const { t } = useI18n();
const { getRequest } = useApiHelpers();
const { getOrderSummary } = useOrderEndpoints();
const route = useRoute();

const coinDepositType = ref("");
const isOrderPreparing = ref(true);
const orderId = ref([]);
const orderData = reactive({
  coinAmount: null,
  coinType: null,
  fiatAmount: null,
  fiatType: null,
  walletAddress: null,
  blockchain: null,
  nativeSellDetails: null,
});

const loadDynamicFormPath = computed(() => {
  const depositType = coinDepositType.value;
  if (!depositType || depositType === '') {
    const error = useErrorStore();
    error.$state.error.code = '5030';
    error.$state.error.message = t('CoinDepositForm.error.sellWalletType');
    router.push({
      name: "error",
    });
    return;
  }

  const formatDepositType = capitaliseFirstChar(depositType.toLowerCase());
  return defineAsyncComponent(() =>
    import(
      /* @vite-ignore */ `./CoinDeposit/${formatDepositType}/${formatDepositType}Form.vue`
    )
  );
});

const handleFormRedirect = (orderId) => {
  router.push(`/status/${orderId}`);
};

const coinDepositFormMapping = () => {
  const sellWalletType = globalStore.getMerchantSettings?.sellWalletType;
  switch(sellWalletType) {
    case "BTC":
      return "BTCDEPOSIT";
    case "HASHPACK":
      return "HASHPACKDEPOSIT";
    case "ZKSYNC":
      return "ZKSYNCDEPOSIT";
    case 'METAMASK':
      return 'METAMASKDEPOSIT';
    case 'NATIVE':
      return 'NATIVEDEPOSIT';
    default:
      return '';
  }
};

const getCoinDepositAction = async () => {
  if (!route.params.orderId) {
    const orderIdResponse = await getRequest("/orderId");
    orderId.value = orderIdResponse.data.orderId;
  } else {
    orderId.value = route.params.orderId;
  }

  const getOrderStatusPaymentData = await getOrderSummary(orderId.value);
  const paymentLabel =
    getOrderStatusPaymentData?.data?.data?.status?.payment?.label;
  const banxaWalletAddress = getOrderStatusPaymentData?.data?.data?.summary?.walletAddress;
  const isPaymentReady = paymentLabel !== 'Preparing Order'
    && banxaWalletAddress !== null
    && getOrderStatusPaymentData.status === 200;

  if (!isPaymentReady) {
    setTimeout(() => {
      getCoinDepositAction();
    }, 30000);
    return;
  }
  if (
    paymentLabel !== 'Action Required'
    || globalStore.getMerchantSettings?.usesCustodialWallet
  ) {
    // If there is no action required, redirect to order summary page
    handleFormRedirect(orderId.value);
    return;
  }

  orderData.coinType = getOrderStatusPaymentData?.data?.data?.summary?.coinType;
  orderData.coinAmount = getOrderStatusPaymentData?.data?.data?.summary?.amountReceived;
  orderData.fiatType = getOrderStatusPaymentData?.data?.data?.summary?.fiatType;
  orderData.fiatAmount = getOrderStatusPaymentData?.data?.data?.summary?.amountPaidOriginal;
  orderData.walletAddress = getOrderStatusPaymentData?.data?.data?.summary?.walletAddress;
  orderData.blockchain = getOrderStatusPaymentData?.data?.data?.summary?.blockchain;
  orderData.nativeSellDetails = getOrderStatusPaymentData?.data?.data?.summary?.nativeSellDetails;
  coinDepositType.value = coinDepositFormMapping(
    orderData.coinType,
    orderData.blockchain?.code,
  );
  isOrderPreparing.value = false;
};

onMounted(async () => {
  getCoinDepositAction();
});
</script>
<style lang="scss"></style>
